import React from "react";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, PlayCircle } from "lucide-react";
import CohortBanner from "@/components/CohortBanner";
import { CareerJourneyTimeline } from "./CareerJourneyTimeline";

/** Section 1 — Hero: emotional headline, 2 CTAs, animated Career Journey visual. */
export const HeroSection = ({ onWatchDemo }) => {
  return (
    <section className="relative pt-32 sm:pt-40 pb-16 px-6" data-testid="homepage-hero-section">
      <div className="absolute inset-0 opacity-60 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 15% 20%, #5C9DFF14, transparent 45%), radial-gradient(circle at 85% 75%, #B58BFF10, transparent 45%)" }} />
      <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 mb-6 text-xs font-bold uppercase tracking-[0.2em] text-[#5C9DFF]">
            <Sparkles className="w-3.5 h-3.5" /> The AI-Powered Career Operating System
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }} className="mb-6">
            <CohortBanner />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05]" data-testid="hero-headline"
          >
            Learn. Practice.<br />Prove. <span className="text-gradient-brand">Get Hired.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="mt-6 text-base sm:text-lg text-slate-300 max-w-xl">
            Everything you need to build your career with AI — from resume creation to verified skills and job opportunities.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }} className="mt-10 flex flex-row items-center gap-4">
            <a href="/register" data-testid="hero-start-free-btn" className="group flex items-center justify-center gap-2 w-[188px] shrink-0 whitespace-nowrap bg-primary hover:bg-primary/90 text-white font-semibold px-5 py-3.5 rounded-xl shadow-[0_0_30px_rgba(92,157,255,0.4)] transition-all hover:-translate-y-0.5">
              Start Free <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <button onClick={onWatchDemo} data-testid="hero-watch-demo-btn" className="flex items-center justify-center gap-2 w-[188px] shrink-0 whitespace-nowrap bg-white/5 hover:bg-white/10 border border-white/15 text-white font-semibold px-5 py-3.5 rounded-xl transition-all hover:-translate-y-0.5">
              <PlayCircle className="w-4 h-4" /> Watch Demo
            </button>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.25 }} className="relative">
          <CareerJourneyTimeline variant="compact" />
        </motion.div>
      </div>
    </section>
  );
};
